import { useFlags } from 'launchdarkly-react-client-sdk';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { sendAnalyticsInfo } from 'features/analytics/helpers';
import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import {
  fetchPreviousCalls,
  selectPreviousCalls,
} from 'features/call/previous-calls/store';
import { previousCallToBeReportedSelected } from 'features/call/vrs-call-comments/store';
import { userSelector } from 'features/user/store';

export function usePreviousCalls() {
  const dispatch = useAppDispatch();
  const previousCallsResult = useAppSelector(selectPreviousCalls);
  const previousCalls = useMemo(
    () => previousCallsResult ?? [],
    [previousCallsResult]
  );
  const userId = useAppSelector(userSelector)?.id;
  const { previousCallReportingEnabled } = useFlags();

  const lastReportedSelectedCallIdRef = useRef<number | undefined>(undefined);

  // Track the user's explicit selection while deriving the effective selection from live data.
  const [requestedSelectedCallId, setRequestedSelectedCallId] = useState<
    number | undefined
  >(undefined);

  // Fetch once we have a user ID.
  useEffect(() => {
    if (userId && previousCallReportingEnabled) {
      dispatch(fetchPreviousCalls());
    }
  }, [dispatch, previousCallReportingEnabled, userId]);

  // Keep the effective selection valid when data changes.
  const selectedCallId = useMemo(() => {
    if (previousCalls.length === 0) {
      return undefined;
    }

    const requestedCallStillExists = previousCalls.some(
      (call) => call.callId === requestedSelectedCallId
    );

    if (requestedSelectedCallId !== undefined && requestedCallStillExists) {
      return requestedSelectedCallId;
    }

    return previousCalls[0].callId;
  }, [previousCalls, requestedSelectedCallId]);

  useEffect(() => {
    if (selectedCallId === undefined) {
      lastReportedSelectedCallIdRef.current = undefined;
      return;
    }

    if (lastReportedSelectedCallIdRef.current === selectedCallId) {
      return;
    }

    dispatch(previousCallToBeReportedSelected(selectedCallId));
    lastReportedSelectedCallIdRef.current = selectedCallId;
  }, [dispatch, selectedCallId]);

  // Debounced analytics to prevent rapid-fire events
  const debouncedAnalytics = useMemo(
    () =>
      debounce((callId: number) => {
        dispatch(
          sendAnalyticsInfo({
            callId,
            event: 'previous_call_reported_call_selected',
            Method: 'selectPreviousCallFromDropdown',
          })
        );
      }, 300),
    [dispatch]
  );

  useEffect(() => {
    return () => {
      debouncedAnalytics.cancel();
    };
  }, [debouncedAnalytics]);

  // User selects a call from the UI.
  const selectCall = useCallback(
    (id: string | number) => {
      const callId = typeof id === 'string' ? Number.parseInt(id, 10) : id;

      if (!Number.isFinite(callId) || callId === selectedCallId) {
        return;
      }
      const selected = previousCalls.find((c) => c.callId === callId);
      if (!selected) {
        console.warn('Attempted to select non-existent call:', id);
        return;
      }

      setRequestedSelectedCallId(callId);
      debouncedAnalytics(selected.callId);
    },
    [previousCalls, selectedCallId, debouncedAnalytics]
  );

  // Derive the selected object without extra state.
  const selectedCall = useMemo(
    () => previousCalls.find((c) => c.callId === selectedCallId) || null,
    [previousCalls, selectedCallId]
  );

  return {
    previousCalls,
    selectedCallId,
    selectedCall,
    selectCall,
    isEmpty: previousCalls.length === 0,
  };
}
